import { useMemo } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import { useHistory } from 'react-router'
import { path } from 'ramda'
import { useSelector } from '@/hooks/redux'
import { usePersonaIdVerification } from '@/features/Persona'
import { showVerifyBusinessLicenceModal } from '@/features/Profile/VerifyBusinessLicenceModal'
import useProfile from '@/hooks/use-profile'
import { useToaster, useCheckTasksCompleted } from '@/models/toaster/hooks'
import { Button } from '@/components/button'
import {
  ListingIcon,
  TenantsIcon,
  ProfileIcon,
  ChatIcon,
  SearchIcon,
  DollarIcon,
  ContractIcon,
  ApplicationIcon
} from '@/components/icons'
import { Box, Stack } from '@/components/structure'
import { TaskKey, Tasks, Task, Step } from './types'

function addIf(cond: boolean, data: Step): Step[]
function addIf(cond: boolean, data: Task): Task[]
function addIf(cond: boolean, data: unknown): unknown[] {
  return cond ? [data] : []
}
function addArrayIf(cond: boolean, data: Task[]): Task[] {
  return cond ? [...data] : []
}

const ViewApplicationButton: React.FC<Nothing> = () => {
  const { t } = useTranslation()
  const id = useSelector(
    path(['toaster', 'valid_tour_items', 'listing_yes_application_no_contract', 'listing_id'])
  )
  const to = typeof id !== 'undefined' ? `/listing/${id}/applications` : '/listings'
  return (
    <Button theme="text" to={to} size="small" space="none">
      {t('b.toaster.view.application.trust_score.view_applications', 'View Applications')}
    </Button>
  )
}

const ContractDescription = () => {
  const { t } = useTranslation()
  const toaster = useToaster()
  const history = useHistory()
  const id = useSelector(
    path(['toaster', 'valid_tour_items', 'listing_yes_application_no_contract', 'listing_id'])
  )
  const to = typeof id !== 'undefined' ? `/listing/${id}` : '/listings'
  const contractTour = () => {
    toaster.updateState('complete_contract.create_contract', {
      stowaway: true,
      data: { highlight: 'contract' }
    })
    history.push(to)
  }
  const applicationTour = () => {
    toaster.updateState('complete_contract.create_contract', {
      stowaway: true,
      data: { highlight: 'application' }
    })
    history.push(to)
  }
  return (
    <Stack gutter={4}>
      <Box display="block">
        {t(
          'b.toaster.view.complete_contract.create_contract.description',
          'Receive an application through liv.rent?'
        )}{' '}
        <Button theme="text" onClick={applicationTour} size="small" space="none">
          {t('b.toaster.view.complete_contract.create_contract.button', 'Create a contract here.')}
        </Button>
      </Box>
      <Box display="block">
        {t(
          'b.toaster.view.complete_contract.create_contract.subdescription',
          'Find a tenant outside of liv.rent?'
        )}{' '}
        <Button theme="text" onClick={contractTour} size="small" space="none">
          {t('b.toaster.view.complete_contract.create_contract.button', 'Create a contract here.')}
        </Button>
      </Box>
    </Stack>
  )
}

const SuggestedButton: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const id = useSelector(path(['toaster', 'valid_tour_items', 'listing_published', 'listing_id']))
  const hasListing = typeof id !== 'undefined'
  const to = `/listing/${id}/suggested-tenants`
  return hasListing ? (
    <Button theme="text" to={to} size="small" space="none">
      {children}
    </Button>
  ) : (
    <span>{children}</span>
  )
}

const enableApplicationTour = false
const enableContractTour = false

const useContent = (): Tasks => {
  const toaster = useToaster()
  const history = useHistory()
  const { t } = useTranslation()
  const profile = useProfile()
  const personaIdVerification = usePersonaIdVerification()
  const isBusiness = profile?.account_type === 'business'
  const isEssentials = profile?.account_type === 'personal'
  const preApprove = profile?.company_info?.listings_pre_approved
  const listingPreVerified = preApprove === '1' || preApprove === 1

  const initialTasks: TaskKey[] = !isEssentials
    ? ['verify_profile', 'publish_listing', 'invite_team']
    : ['verify_profile', 'publish_listing']
  const initialTasksComplete = useCheckTasksCompleted(initialTasks)

  const data = useMemo(() => {
    const tasks: Tasks = {
      title: t('b.toaster.view.get_started.title', 'Get started with liv.rent'),
      description: t(
        'b.toaster.view.follow_onboarding_guide.text',
        'Follow our onboarding guide to quickly streamline your rental process.'
      ),
      tasks: [
        {
          key: 'verify_profile',
          title: t('b.toaster.view.verify_profile.title', 'Verify your profile'),
          description: isBusiness
            ? t(
                'b.toaster.view.verify_profile.description_license',
                'Improve your credibility and attract more renters by verifying your ID, email and business license.'
              )
            : t(
                'b.toaster.view.verify_profile.description',
                'Improve your credibility and attract more renters by verifying your ID and email.'
              ),
          icon: ProfileIcon,
          steps: [
            {
              key: 'id',
              title: t('b.toaster.view.verify_profile.id.title', 'Add photo ID'),
              description: '',
              onClick: () => personaIdVerification({})
            },
            {
              key: 'email',
              title: t('b.toaster.view.verify_profile.email.title', 'Verify your email'),
              description: '',
              onClick: () => {},
              tooltip: state => {
                if (state === 'incomplete')
                  return {
                    openOnClick: true,
                    content: t(
                      'b.toaster.view.verify_profile.email.tooltip',
                      'Please check your email to verify.'
                    )
                  }
                return null
              }
            },
            ...addIf(isBusiness, {
              key: 'license',
              title: t(
                'b.toaster.view.verify_profile.license.title',
                'Verify your business license'
              ),
              description: '',
              onClick: () => showVerifyBusinessLicenceModal({}),
              tooltip: state => {
                if (state === 'locked')
                  return {
                    content: t(
                      'b.toaster.view.verify_profile.license.tooltip',
                      'Please check your email to verify.'
                    )
                  }
                return null
              }
            })
          ]
        },
        {
          key: 'publish_listing',
          title: t('b.toaster.view.publish_listing.title', 'Publish listing'),
          description: t(
            'b.toaster.view.publish_listing.description',
            'Your listings are where your entire rental journey on liv.rent begins.'
          ),
          icon: ListingIcon,
          steps: [
            {
              key: 'create',
              title: t('b.toaster.view.publish_listing.create.title', 'Create new listing'),
              description: '',
              to: '/listing/create'
            },
            ...addIf(!listingPreVerified, {
              key: 'verify',
              title: t('b.toaster.view.publish_listing.verify.title', 'Verify listing'),
              description: state => {
                if (state === 'incomplete')
                  return t(
                    'b.toaster.view.publish_listing.verify.description',
                    'Become a trusted landlord and improve your credibility with renters.'
                  )
                return ''
              },
              onClick: (data, items) => {
                const id = items?.listing_published_unverified
                  ? items?.listing_published_unverified?.listing_id
                  : false
                history.push(typeof id === 'string' ? `/listing/verify/${id}` : `/listings`)
              },
              tooltip: state => {
                if (state === 'locked')
                  return {
                    content: t(
                      'b.toaster.view.publish_listing.verify.tooltip',
                      'A published listing is required.'
                    )
                  }
                return null
              }
            }),
            {
              key: 'share',
              title: t('b.toaster.view.publish_listing.share.title', 'Share OR Advertise listing'),
              description: '',
              onClick: (data, items) => {
                const id = items?.listing_published ? items?.listing_published?.listing_id : false
                history.push(typeof id === 'string' ? `/listing/${id}?show=share` : `/listings`)
              },
              tooltip: state => {
                if (state === 'locked')
                  return {
                    content: t(
                      'b.toaster.view.publish_listing.verify.tooltip',
                      'A published listing is required.'
                    )
                  }
                return null
              }
            }
          ]
        },
        ...addIf(!isEssentials, {
          key: 'invite_team',
          title: t('b.toaster.view.invite_team.title', 'Invite your team'),
          description: t(
            'b.toaster.view.invite_team.description',
            'Do you have multiple agents that manage a listing? Add, remove, or modify your team.'
          ),
          icon: TenantsIcon,
          steps: [
            {
              key: 'invite_team',
              title: t('b.toaster.view.invite_team.invite_team.title', 'Send invitations'),
              description: '',
              onClick: () => {
                toaster.updateState('invite_team.invite_team', {
                  stowaway: true,
                  step: {
                    state: 'complete'
                  },
                  data: { highlight: true }
                })
                history.push('/manage/users')
              }
            }
          ]
        }),
        ...addArrayIf(initialTasksComplete, [
          {
            key: 'find_tenants',
            title: t('b.toaster.view.find_tenants.title', 'Find quality tenants faster'),
            description: t(
              'b.toaster.view.find_tenants.description',
              'Chat instantly with ID verified renters & search from our list of suggested tenants.'
            ),
            icon: SearchIcon,
            steps: [
              {
                key: 'find_tenants',
                title: t('b.toaster.view.find_tenants.find_tenants.title', 'Take a look'),
                description: '',
                onClick: () => {
                  toaster.updateState('find_tenants.find_tenants', {
                    stowaway: true,
                    step: {
                      state: 'complete'
                    },
                    data: { highlight: 'listing' }
                  })
                  history.push('/listings')
                }
              }
            ]
          },
          {
            key: 'chat_leads',
            title: t('b.toaster.view.chat_leads.title', 'Chat with leads and applicants'),
            description: t(
              'b.toaster.view.chat_leads.description',
              'Centralize your communication with leads, applicants, and tenants. Keep all the information you need in one place.'
            ),
            icon: ChatIcon,
            steps: [
              {
                key: 'view',
                title: t('b.toaster.view.chat_leads.view.title', 'View your first inquiry'),
                description: state => {
                  if (state === 'locked')
                    return (
                      <Trans i18nKey="b.toaster.view.chat_leads.view.description" t={t}>
                        Initiate a chat with one of our{' '}
                        <SuggestedButton>suggested tenants</SuggestedButton>, or wait for a
                        prospective tenant to reach out.
                      </Trans>
                    )
                  return ''
                },
                onClick: () => {
                  toaster.updateState('chat_leads.view', {
                    stowaway: true,
                    step: {
                      state: 'complete'
                    }
                  })
                  history.push('/chat')
                },
                tooltip: state => {
                  if (state === 'locked')
                    return {
                      content: t(
                        'b.toaster.view.chat_leads.view.tooltip',
                        'You haven’t received any chat inquiries yet!'
                      )
                    }
                  return null
                }
              },
              {
                key: 'create',
                title: t('b.toaster.view.chat_leads.create.title', 'Create and edit quick replies'),
                description: t(
                  'b.toaster.view.chat_leads.create.description',
                  'Save time replying to inquiries with our quick reply templates.'
                ),
                onClick: () => {
                  toaster.updateState('chat_leads.create', {
                    stowaway: true,
                    step: {
                      state: 'complete'
                    },
                    data: {
                      highlight: true
                    }
                  })
                  history.push('/chat?tour=reply')
                }
              }
            ]
          },
          ...addIf(enableApplicationTour, {
            key: 'application',
            title: t('b.toaster.view.application.title', 'Receive your first application'),
            description: t(
              'b.toaster.view.application.description',
              'Compare applicants and find your best option through liv.rent.'
            ),
            icon: ApplicationIcon,
            steps: [
              {
                key: 'invite',
                title: t('b.toaster.view.application.invite.title', 'Invite to apply'),
                description: state => {
                  if (state === 'complete') {
                    return ''
                  } else {
                    return t(
                      'b.toaster.view.application.invite.description',
                      'After receiving an inquiry, you can invite them to apply directly through our chat.'
                    )
                  }
                },
                onClick: () => {
                  toaster.updateState('application.invite', {
                    stowaway: true,
                    step: {
                      state: 'complete'
                    },
                    data: {
                      highlight: 'sidebar'
                    }
                  })
                  history.push('/chat')
                },
                tooltip: state => {
                  if (state === 'locked')
                    return {
                      content: t(
                        'b.toaster.view.application.invite.tooltip',
                        'You haven’t received any chat inquiries yet!'
                      )
                    }
                  return null
                }
              },
              {
                key: 'received',
                title: t('b.toaster.view.application.received.title', 'First application received'),
                description: state => {
                  if (state === 'incomplete')
                    return t(
                      'b.toaster.view.application.received.description',
                      'You will receive an email and a chat message from interested leads who have applied.'
                    )
                  return ''
                }
              },
              {
                key: 'trust_score',
                title: t(
                  'b.toaster.view.application.trust_score.title',
                  'Unlock first Trust Score'
                ),
                description: state => {
                  if (state === 'complete') {
                    return <ViewApplicationButton />
                  } else if (state === 'incomplete') {
                    return (
                      <>
                        {t(
                          'b.toaster.view.application.trust_score.description_incomplete',
                          'Choose applicants that interest you and unlock their Trust Score. Please note that you can do this at any time.'
                        )}
                        <Box top={4}>
                          <ViewApplicationButton />
                        </Box>
                      </>
                    )
                  } else {
                    return (
                      <Trans
                        i18nKey="b.toaster.view.application.trust_score.description_locked"
                        t={t}
                      >
                        Screen tenants efficiently with our{' '}
                        <Button
                          theme="text"
                          href="https://liv.rent/blog/2019/10/trust-score-explained/"
                          size="small"
                          space="none"
                        >
                          Trust Score
                        </Button>
                        , our intelligent rating system powered by credit checks, employment
                        documents and more.
                      </Trans>
                    )
                  }
                },
                onClick: () => {
                  toaster.updateState('application.trust_score', {
                    stowaway: true,
                    step: {
                      state: 'complete'
                    },
                    data: {
                      highlight: 'listing'
                    }
                  })
                  history.push('/listings')
                },
                tooltip: state => {
                  if (state === 'locked')
                    return {
                      content: t(
                        'b.toaster.view.application.trust_score.tooltip',
                        'You haven’t received any applications yet!'
                      )
                    }
                  return null
                }
              }
            ]
          }),
          {
            key: 'setup_pad',
            title: t('b.toaster.view.setup_pad.title', 'Setup rental payments'),
            description: (
              <>
                <Box>
                  {t(
                    'b.toaster.view.setup_pad.description',
                    'You can request payment for rent or other rental deposits directly with liv.rent. Tenants have the option to pay by credit card, Union Pay or bitcoin.  '
                  )}
                </Box>
                <Box>
                  {t(
                    'b.toaster.view.setup_pad.subdescription',
                    'Please select a payment method to setup your rental payments.'
                  )}
                </Box>
              </>
            ),
            icon: DollarIcon,
            steps: [
              {
                key: 'setup_pad',
                title: state =>
                  state !== 'complete'
                    ? t('b.toaster.view.setup_pad.setup_pad.add.button', 'Add payment method')
                    : t('b.toaster.view.setup_pad.setup_pad.view.button', 'View payment settings'),
                description: '',
                onClick: () => {
                  toaster.updateState('setup_pad.setup_pad', {
                    stowaway: true,
                    step: {
                      state: 'complete'
                    }
                  })
                  history.push('/settings/payment-settings')
                }
              }
            ]
          },
          ...addIf(enableContractTour, {
            key: 'complete_contract',
            title: t('b.toaster.view.complete_contract.title', 'Complete your first contract'),
            description: t(
              'b.toaster.view.complete_contract.description',
              'Ensure an error-free application process by using our digital contracts. Use our ready-to-use tenancy agreements or upload your own (coming soon).'
            ),
            icon: ContractIcon,
            complete: (
              <Button theme="text" to="/settings/payment-settings" size="small" space="none">
                {t('b.toaster.view.complete_contract.button', 'View completed contract')}
              </Button>
            ),
            steps: [
              {
                key: 'create_contract',
                title: t(
                  'b.toaster.view.complete_contract.create_contract.title',
                  'Create a contract'
                ),
                description: state =>
                  state !== 'complete' ? <ContractDescription state={state} /> : ''
              },
              ...addIf(!isEssentials, {
                key: 'add_addendum',
                title: t(
                  'b.toaster.view.complete_contract.add_addendum.title',
                  'Add your first addendum'
                ),
                description: state => {
                  if (state === 'locked' || state === 'complete') return ''
                  return t(
                    'b.toaster.view.complete_contract.add_addendum.description',
                    'Create and save reusable customized addendums to be auto integrated into your digital standard lease agreement.'
                  )
                },
                tooltip: state => {
                  if (state === 'locked')
                    return {
                      content: t(
                        'b.toaster.view.complete_contract.add_addendum.tooltip',
                        'A contract is required.'
                      )
                    }
                  return null
                }
              }),
              {
                key: 'send_contract',
                title: t('b.toaster.view.complete_contract.send_contract.title', 'Send a contract'),
                description: state => {
                  if (state === 'locked' || state === 'complete') return ''
                  return t(
                    'b.toaster.view.complete_contract.send_contract.description',
                    'You can easily send a contract on the listing dashboard through the applications or contracts page.'
                  )
                }
              },
              {
                key: 'sign_contract',
                title: t('b.toaster.view.complete_contract.sign_contract.title', 'Sign a contract'),
                description: (state, badge) => {
                  if (state === 'complete') return ''
                  if (state === 'locked' && badge === 'pending')
                    return t(
                      'b.toaster.view.complete_contract.sign_contract.locked.description',
                      'Waiting for the tenants to sign...'
                    )
                  if (state === 'locked') return ''
                  return t(
                    'b.toaster.view.complete_contract.sign_contract.description',
                    'The tenants have signed! It’s your turn to sign the contract.'
                  )
                }
              }
            ]
          })
        ])
      ]
    }
    return tasks
  }, [
    t,
    isBusiness,
    isEssentials,
    initialTasksComplete,
    toaster,
    history,
    listingPreVerified,
    personaIdVerification
  ])
  return data
}

export default useContent
